/**
 * Metro 2 Letter Package Builder Component
 * 
 * Professional Metro 2 package selector with:
 * - AI recommended package
 * - 17 professional Metro 2-specific package types
 * - No war-like/gimmicky names
 * - Package subtitles explaining use cases
 * - Confidence scoring
 * - Custom configuration options
 */

import React, { useState } from 'react';
import {
  Sparkles,
  FileText,
  Layers,
  SplitSquareHorizontal,
  Grid,
  Building2,
  AlertTriangle,
  FileCheck,
  ArrowUpCircle,
  Copy,
  FileSearch,
  FileUp,
  GraduationCap,
  DollarSign,
  Calendar,
  Settings,
  ChevronDown,
  ChevronUp,
  Check,
  Info,
  Zap,
  HelpCircle,
  Scale,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';

// Professional Metro 2 Package Types
const METRO2_PACKAGE_TYPES = [
  {
    key: 'ai_recommended',
    label: 'AI Recommended Package',
    subtitle: 'Let the system choose the strongest Metro 2 package style based on selected items, bureaus, and reporting issues.',
    icon: Sparkles,
    category: 'recommended',
    confidence: null, // Will be set dynamically
  },
  {
    key: 'bureau_level',
    label: 'Bureau-Level Technical Package',
    subtitle: 'One strong Metro 2 letter per bureau with all selected technical issues grouped clearly.',
    icon: FileText,
    category: 'standard',
    confidence: 90,
  },
  {
    key: 'account_focused',
    label: 'Account-Focused Technical Package',
    subtitle: 'Generates a separate focused Metro 2 letter for each selected account.',
    icon: Layers,
    category: 'standard',
    confidence: 85,
  },
  {
    key: 'smart_split',
    label: 'Smart Split Package',
    subtitle: 'Splits large disputes into smaller, cleaner Metro 2 letters based on item limits.',
    icon: SplitSquareHorizontal,
    category: 'standard',
    confidence: 88,
  },
  {
    key: 'issue_type',
    label: 'Issue-Type Package',
    subtitle: 'Creates separate letters by Metro 2 issue category, such as status, balance, dates, or payment history.',
    icon: Grid,
    category: 'advanced',
    confidence: 82,
  },
  {
    key: 'account_only',
    label: 'Account-Only Metro 2 Package',
    subtitle: 'Uses only selected account tradelines with Metro 2 reporting conflicts.',
    icon: FileCheck,
    category: 'standard',
    confidence: 80,
  },
  {
    key: 'collection_reporting',
    label: 'Collection Reporting Package',
    subtitle: 'Focuses on collection accounts with technical reporting inconsistencies.',
    icon: Building2,
    category: 'specialized',
    confidence: 87,
  },
  {
    key: 'furnisher_specific',
    label: 'Furnisher-Specific Package',
    subtitle: 'Groups disputes by creditor or furnisher when the same company reports inconsistent data.',
    icon: Building2,
    category: 'advanced',
    confidence: 84,
  },
  {
    key: 'violation_heavy',
    label: 'Violation-Heavy Package',
    subtitle: 'Best for advanced technical disputes with multiple Metro 2 field conflicts or code-level issues.',
    icon: AlertTriangle,
    category: 'advanced',
    confidence: 92,
  },
  {
    key: 'concise_first_round',
    label: 'Concise First-Round Package',
    subtitle: 'Shorter bureau-specific letters for clean first-round Metro 2 disputes.',
    icon: FileText,
    category: 'standard',
    confidence: 88,
  },
  {
    key: 'escalation_mov',
    label: 'Escalation / MOV Package',
    subtitle: 'Best for Round 2 or Round 3 disputes, reinvestigation failures, or method-of-verification demands.',
    icon: ArrowUpCircle,
    category: 'escalation',
    confidence: 95,
  },
  {
    key: 'duplicate_reinserted',
    label: 'Duplicate / Reinserted Reporting Package',
    subtitle: 'Focuses on duplicate accounts, reinserted items, or repeated inconsistent reporting.',
    icon: Copy,
    category: 'specialized',
    confidence: 90,
  },
  {
    key: 'verification_demand',
    label: 'Verification Demand Package',
    subtitle: 'Best when the issue requires field-level verification but is not yet a confirmed conflict.',
    icon: FileSearch,
    category: 'verification',
    confidence: 85,
  },
  {
    key: 'evidence_required',
    label: 'Evidence-Required Package',
    subtitle: 'Best when the dispute should reference supporting documents or request evidence-based review.',
    icon: FileUp,
    category: 'evidence',
    confidence: 83,
  },
  {
    key: 'student_loan_timeline',
    label: 'Student Loan Timeline Package',
    subtitle: 'Focuses on student loan reporting involving deferment, forbearance, transfer, IDR, or disputed late-payment timelines.',
    icon: GraduationCap,
    category: 'specialized',
    confidence: 88,
  },
  {
    key: 'chargeoff_ownership',
    label: 'Charge-Off Ownership Package',
    subtitle: 'Focuses on charge-off reporting where balance, ownership, sold/transfer status, or duplicate collection reporting needs review.',
    icon: DollarSign,
    category: 'specialized',
    confidence: 86,
  },
  {
    key: 'dofd_reaging',
    label: 'DOFD / Re-Aging Package',
    subtitle: 'Focuses on Date of First Delinquency, obsolete reporting, collection-opened dates, or re-aging concerns.',
    icon: Calendar,
    category: 'specialized',
    confidence: 89,
  },
];

// Letter styles
const LETTER_STYLES = [
  {
    key: 'advanced',
    label: 'Advanced',
    badge: 'Recommended for most Metro 2 disputes',
    includes: 'Detailed Data Tables + Your Reasons & Instructions + Items Summary + Violation Descriptions',
    description: 'Best for professional Metro 2 disputes where the letter needs strong technical detail without becoming too long.',
  },
  {
    key: 'summary',
    label: 'Summary',
    badge: null,
    includes: 'Detailed Data Tables + Your Reasons & Instructions + Items Summary',
    description: 'Best for first-round disputes where clarity and readability matter most.',
  },
  {
    key: 'everything',
    label: 'Everything',
    badge: null,
    includes: 'Detailed Data Tables + Your Reasons & Instructions + Items Summary + Detailed Individual Violation Descriptions',
    description: 'Best for complex files, repeat disputes, escalation rounds, or agency-level technical review. This may produce more pages.',
  },
  {
    key: 'basic',
    label: 'Basic',
    badge: null,
    includes: 'Standard Data Tables + Your Reasons & Instructions',
    description: 'Best for simple first-round disputes with only a few technical inconsistencies.',
  },
];

// Category colors
const CATEGORY_COLORS = {
  recommended: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
  standard: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  advanced: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700',
  specialized: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  escalation: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  verification: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700',
  evidence: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700',
};

// AI Recommendation Panel
const AIRecommendationPanel = ({ recommendation, onApply }) => {
  if (!recommendation) return null;

  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border-emerald-200 dark:border-emerald-700/50 mb-6" data-testid="ai-package-recommendation">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-800/50">
              <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <CardTitle className="text-lg text-emerald-900 dark:text-emerald-200">AI Recommended Setup</CardTitle>
          </div>
          <Badge className="bg-emerald-600 text-white">
            {recommendation.confidence_score}% confidence
          </Badge>
        </div>
        <CardDescription className="text-emerald-700 dark:text-emerald-400">
          Based on your selected accounts, bureaus, and issue categories.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recommendation Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/80 dark:bg-stone-900/50 rounded-lg p-3">
            <div className="text-xs text-stone-500 dark:text-stone-400 mb-1">Letter Type</div>
            <div className="font-medium text-stone-900 dark:text-white text-sm">
              {recommendation.recommended_letter_type_label || 'Bureau-Level'}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-stone-900/50 rounded-lg p-3">
            <div className="text-xs text-stone-500 dark:text-stone-400 mb-1">Letter Style</div>
            <div className="font-medium text-stone-900 dark:text-white text-sm capitalize">
              {recommendation.recommended_letter_style || 'Advanced'}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-stone-900/50 rounded-lg p-3">
            <div className="text-xs text-stone-500 dark:text-stone-400 mb-1">Items/Letter</div>
            <div className="font-medium text-stone-900 dark:text-white text-sm capitalize">
              {recommendation.recommended_account_limit || 'AI Optimized'}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-stone-900/50 rounded-lg p-3">
            <div className="text-xs text-stone-500 dark:text-stone-400 mb-1">Total Letters</div>
            <div className="font-medium text-stone-900 dark:text-white text-sm">
              {recommendation.package_estimate?.total_letters || 3}
            </div>
          </div>
        </div>

        {/* Reasoning */}
        {recommendation.reasoning?.length > 0 && (
          <div className="bg-white/60 dark:bg-stone-900/30 rounded-lg p-3">
            <div className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 mb-1">
              <HelpCircle className="w-3 h-3" />
              Why this recommendation?
            </div>
            <p className="text-sm text-stone-700 dark:text-stone-300">
              {recommendation.reasoning[0]}
            </p>
          </div>
        )}

        {/* Legal Authority & Field Mapping Preview */}
        {(recommendation.legal_authorities?.length > 0 || recommendation.field_mappings?.length > 0) && (
          <div className="flex gap-4 text-xs">
            {recommendation.legal_authorities?.length > 0 && (
              <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Scale className="w-3 h-3" />
                {recommendation.legal_authorities.length} Legal Authorities
              </div>
            )}
            {recommendation.field_mappings?.length > 0 && (
              <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                <Grid className="w-3 h-3" />
                {recommendation.field_mappings.length} Field Mappings
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button onClick={onApply} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="use-ai-recommendation-btn">
            <Zap className="w-4 h-4 mr-2" />
            Use AI Recommendation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Package Type Selector
const PackageTypeSelector = ({ selectedType, onSelect, aiConfidence }) => {
  const [expandedCategory, setExpandedCategory] = useState('recommended');

  // Group packages by category
  const categories = {
    recommended: METRO2_PACKAGE_TYPES.filter(p => p.category === 'recommended'),
    standard: METRO2_PACKAGE_TYPES.filter(p => p.category === 'standard'),
    advanced: METRO2_PACKAGE_TYPES.filter(p => p.category === 'advanced'),
    specialized: METRO2_PACKAGE_TYPES.filter(p => p.category === 'specialized'),
    escalation: METRO2_PACKAGE_TYPES.filter(p => p.category === 'escalation'),
    verification: METRO2_PACKAGE_TYPES.filter(p => p.category === 'verification'),
    evidence: METRO2_PACKAGE_TYPES.filter(p => p.category === 'evidence'),
  };

  const categoryLabels = {
    recommended: 'AI Recommended',
    standard: 'Standard Packages',
    advanced: 'Advanced Packages',
    specialized: 'Specialized Packages',
    escalation: 'Escalation Packages',
    verification: 'Verification Packages',
    evidence: 'Evidence-Based Packages',
  };

  return (
    <div className="space-y-4" data-testid="package-type-selector">
      <Label className="text-sm font-medium text-stone-700 dark:text-stone-300">Letter Package Type</Label>
      
      {Object.entries(categories).map(([category, packages]) => {
        if (packages.length === 0) return null;
        
        const isExpanded = expandedCategory === category;
        
        return (
          <div key={category} className="border border-stone-200 dark:border-stone-700 rounded-lg overflow-hidden">
            {/* Category Header */}
            <button
              className={`w-full p-3 flex items-center justify-between text-left ${
                isExpanded ? 'bg-stone-100 dark:bg-stone-800' : 'bg-white dark:bg-stone-900'
              }`}
              onClick={() => setExpandedCategory(isExpanded ? null : category)}
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={CATEGORY_COLORS[category]}>
                  {categoryLabels[category]}
                </Badge>
                <span className="text-sm text-stone-500 dark:text-stone-400">
                  ({packages.length} package{packages.length > 1 ? 's' : ''})
                </span>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Package Options */}
            {isExpanded && (
              <div className="p-2 space-y-2">
                {packages.map((pkg) => {
                  const IconComponent = pkg.icon;
                  const isSelected = selectedType === pkg.key;
                  const confidence = pkg.key === 'ai_recommended' ? aiConfidence : pkg.confidence;

                  return (
                    <div
                      key={pkg.key}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-600'
                          : 'border-stone-200 dark:border-stone-700 hover:border-emerald-300 dark:hover:border-emerald-700 bg-white dark:bg-stone-900'
                      }`}
                      onClick={() => onSelect(pkg.key)}
                      data-testid={`package-option-${pkg.key}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-100 dark:bg-emerald-800/50' : 'bg-stone-100 dark:bg-stone-800'}`}>
                          <IconComponent className={`w-4 h-4 ${isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-stone-500 dark:text-stone-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-stone-800 dark:text-stone-200'}`}>
                              {pkg.label}
                            </span>
                            {confidence && (
                              <Badge variant="outline" className="text-xs bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400">
                                {confidence}%
                              </Badge>
                            )}
                            {isSelected && (
                              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 ml-auto" />
                            )}
                          </div>
                          <p className="text-sm text-stone-500 dark:text-stone-400">
                            {pkg.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Letter Style Selector
const LetterStyleSelector = ({ selectedStyle, onSelect }) => {
  return (
    <div className="space-y-3" data-testid="letter-style-selector">
      <Label className="text-sm font-medium text-stone-700 dark:text-stone-300">Letter Style</Label>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {LETTER_STYLES.map((style) => {
          const isSelected = selectedStyle === style.key;

          return (
            <div
              key={style.key}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-600'
                  : 'border-stone-200 dark:border-stone-700 hover:border-emerald-300 dark:hover:border-emerald-700 bg-white dark:bg-stone-900'
              }`}
              onClick={() => onSelect(style.key)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-stone-800 dark:text-stone-200'}`}>
                  {style.label}
                </span>
                <div className="flex items-center gap-2">
                  {style.badge && (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs">
                      {style.badge}
                    </Badge>
                  )}
                  {isSelected && <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                </div>
              </div>
              <p className="text-xs text-stone-500 dark:text-stone-400 mb-2">
                {style.includes}
              </p>
              <p className="text-sm text-stone-600 dark:text-stone-400">
                {style.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Custom Options Panel
const CustomOptionsPanel = ({ 
  maxItemsPerLetter, 
  onMaxItemsChange, 
  includeSignature, 
  onSignatureChange 
}) => {
  return (
    <Card className="border-stone-200 dark:border-stone-700" data-testid="custom-options-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 text-stone-700 dark:text-stone-300">
          <Settings className="w-4 h-4" />
          Custom Options
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Max Items Per Letter */}
          <div>
            <Label className="text-sm mb-2 block">Max Items Per Letter</Label>
            <Select value={maxItemsPerLetter} onValueChange={onMaxItemsChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ai_recommended">AI Recommended</SelectItem>
                <SelectItem value="all">All Items</SelectItem>
                <SelectItem value="3">3 Items</SelectItem>
                <SelectItem value="5">5 Items</SelectItem>
                <SelectItem value="10">10 Items</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Include Signature */}
          <div className="flex items-center space-x-2 pt-6">
            <Checkbox
              id="signature"
              checked={includeSignature}
              onCheckedChange={onSignatureChange}
            />
            <label
              htmlFor="signature"
              className="text-sm text-stone-700 dark:text-stone-300 cursor-pointer"
            >
              Include signature line
            </label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Metro 2 Letter Package Builder Component
const Metro2LetterPackageBuilder = ({
  recommendation,
  selectedPackageType,
  selectedLetterStyle,
  maxItemsPerLetter,
  includeSignature,
  onPackageTypeChange,
  onLetterStyleChange,
  onMaxItemsChange,
  onSignatureChange,
  onApplyAIRecommendation,
  onRestoreAIRecommendation,
  showCustomOptions = true,
}) => {
  const [customOptionsExpanded, setCustomOptionsExpanded] = useState(false);

  return (
    <div className="space-y-6" data-testid="metro2-letter-package-builder">
      {/* AI Recommendation Panel */}
      <AIRecommendationPanel 
        recommendation={recommendation} 
        onApply={onApplyAIRecommendation} 
      />

      {/* Package Type Selector */}
      <PackageTypeSelector
        selectedType={selectedPackageType}
        onSelect={onPackageTypeChange}
        aiConfidence={recommendation?.confidence_score}
      />

      {/* Letter Style Selector */}
      <LetterStyleSelector
        selectedStyle={selectedLetterStyle}
        onSelect={onLetterStyleChange}
      />

      {/* Custom Options Toggle */}
      {showCustomOptions && (
        <div>
          <Button
            variant="ghost"
            className="text-stone-600 dark:text-stone-400 w-full justify-between"
            onClick={() => setCustomOptionsExpanded(!customOptionsExpanded)}
          >
            <span className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Custom Options
            </span>
            {customOptionsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>

          {customOptionsExpanded && (
            <CustomOptionsPanel
              maxItemsPerLetter={maxItemsPerLetter}
              onMaxItemsChange={onMaxItemsChange}
              includeSignature={includeSignature}
              onSignatureChange={onSignatureChange}
            />
          )}
        </div>
      )}

      {/* Restore AI Recommendation */}
      {selectedPackageType !== 'ai_recommended' && recommendation && (
        <Button 
          variant="link" 
          onClick={onRestoreAIRecommendation} 
          className="text-emerald-600 dark:text-emerald-400 p-0"
        >
          <Sparkles className="w-4 h-4 mr-1" />
          Restore AI Recommendation
        </Button>
      )}
    </div>
  );
};

export default Metro2LetterPackageBuilder;
export { 
  AIRecommendationPanel, 
  PackageTypeSelector, 
  LetterStyleSelector, 
  CustomOptionsPanel,
  METRO2_PACKAGE_TYPES,
  LETTER_STYLES,
};
